import React, { useCallback, useEffect } from 'react';

import { defineMessages, useIntl } from 'react-intl';
import { di } from 'react-magnetic-di';

import FeatureGates from '@atlaskit/feature-gate-js-client';
import { fg } from '@atlaskit/platform-feature-flags';
import { Text } from '@atlaskit/primitives/compiled';
import { useAnalytics } from '@atlassian/conversation-assistant-instrumentation/controllers/analytics';

import type { UpdateWorkItemAllFieldTypesAction } from '../../../../../common/types';
import { FieldTooltipContent } from '../../../../../common/ui/FieldTooltipContent';
import {
	type RegisterTooltipProvider,
	useRegisterTooltipContentProvider,
} from '../../../../../common/utils/useRegisterTooltipContentProvider';
import { getActionData } from '../../../utils';

import { toIssueLinksFieldValue } from './utils';

const messages = defineMessages({
	update: {
		id: 'update-work-item-all-fields-action-renderer.ui.action-card.jira-issue-links-field.update',
		defaultMessage: 'Add issue link',
		description: 'Label for the change action in the jira number field',
	},
	updateWithRelationship: {
		id: 'update-work-item-all-fields-action-renderer.ui.action-card.jira-issue-links-field.update-with-relationship.non-final',
		defaultMessage: "Link ''{relationship}'' work item: {issueKey}",
		description:
			'Label for an action that links another work item using a specific issue link relationship (e.g. Relates, Blocks, ...)',
	},
});

export type Props = {
	action: UpdateWorkItemAllFieldTypesAction;
	registerTooltipProvider?: RegisterTooltipProvider;
	issueKey: string;
};

export const IssueLinksField = ({
	action,
	registerTooltipProvider,
	issueKey,
}: Props): React.JSX.Element | null => {
	di(useAnalytics);

	const { formatMessage } = useIntl();
	const { sendAnalyticsEvent } = useAnalytics();

	const { fieldKey, fieldValue, fieldReasoning } = getActionData(action);

	const isFieldRenderable = fieldKey === 'issuelinks';

	const { type, outwardIssue } = toIssueLinksFieldValue(fieldValue) ?? {};

	const shouldNotRenderField = !isFieldRenderable || !type?.name || !outwardIssue?.key;

	// Track when this renders nothing (empty row) due to an unexpected issuelinks shape.
	useEffect(() => {
		if (shouldNotRenderField && fg('jira-track-invalid-update-workitem-actions')) {
			sendAnalyticsEvent({
				eventType: 'operational',
				action: 'detected',
				actionSubject: 'invalidField',
				actionSubjectId: 'updateWorkActionRenderer',
				attributes: {
					fieldType: 'issuelinks',
					anomaly: 'malformedShape',
					invocationId: action.invocationId,
				},
			});
		}
	}, [shouldNotRenderField, action.invocationId, sendAnalyticsEvent]);

	const toolTipContentProvider = useCallback(
		() => (
			<FieldTooltipContent
				reasoning={fieldReasoning}
				suggested={
					type?.name && outwardIssue?.key
						? `${issueKey} ${type?.name.toLowerCase()} ${outwardIssue?.key}`
						: undefined
				}
			/>
		),
		[fieldReasoning, type?.name, outwardIssue?.key, issueKey],
	);

	useRegisterTooltipContentProvider({
		registerTooltipProvider,
		fieldId: fieldKey,
		provider: toolTipContentProvider,
		enabled: isFieldRenderable,
	});

	if (shouldNotRenderField) {
		return null;
	}

	return (
		<Text
			color="color.text.subtle"
			maxLines={1}
			testId="update-work-item-all-fields-action-renderer.ui.action-card.card-fields-list.issue-links-field"
		>
			{FeatureGates.getExperimentValue(
				'jira-ai-update-workitem-field-action-details-exp',
				'isEnabled',
				false,
			)
				? formatMessage(messages.updateWithRelationship, {
						relationship: type.name,
						issueKey: outwardIssue.key,
					})
				: formatMessage(messages.update)}
		</Text>
	);
};
